import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, Grid, MenuItem, TextField } from '@mui/material'
import {
  EMPTY_LIBRARY,
  RNA_CAPTURE_OFF,
  libraryPrepsFor,
  panelRequired,
  preselectSpecimenSource,
  pruneSpecimenSource,
} from '../../lib/rnaLibrary'

// The sequencing library of a tumour RNA analysis, asking only what cannot be
// inferred and changes how it runs: the preparation (Poly(A) preselected, "Not
// sure" sends none), the target panel right under it when the preparation is a
// targeted capture, the RNA input the QC gate reads, and the tumour block.
// "Same tumour block as" shares the tumour specimen of an analysis named under
// "Other analyses of this patient", which pairs the DNA and the RNA; with one
// such analysis it is preselected. The choice only ever shows an analysis that is
// still linked (the wizard clears it when the link goes).

const LibraryFields = ({ library, onChange, errors, linkedAnalyses, capture, panelPicker }) => {
  const preps = libraryPrepsFor(capture)
  const value = { ...EMPTY_LIBRARY, ...(library || {}) }
  const specimenFrom = pruneSpecimenSource(value, linkedAnalyses).specimenFromAnalysisId
  const set = (key) => (e) => onChange({ ...value, [key]: e.target.value })
  const showPanel = panelRequired(value, capture) && panelPicker

  // One linked analysis and no choice of the user's own: same tumour block.
  useEffect(() => {
    const next = preselectSpecimenSource(value, linkedAnalyses)
    if (next !== value) onChange(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedAnalyses, value.specimenFromAnalysisId, value.specimenFromChosen])

  return (
    <Box data-testid="library-fields">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}>
          <TextField
            select
            fullWidth
            size="small"
            required
            label="Library preparation"
            value={preps.some((p) => p.value === value.libraryPrep) ? value.libraryPrep : ''}
            onChange={set('libraryPrep')}
            error={!!errors.libraryPrep}
            helperText={errors.libraryPrep}
            inputProps={{ 'data-testid': 'library-prep' }}
          >
            {preps.map((prep) => (
              <MenuItem key={prep.value} value={prep.value}>
                {prep.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="RNA input (ng)"
            value={value.inputNg}
            onChange={set('inputNg')}
            error={!!errors.inputNg}
            helperText={errors.inputNg || 'Optional'}
            inputProps={{ min: 0, step: 1, 'data-testid': 'library-input-ng' }}
          />
        </Grid>
        {showPanel && (
          <Grid item xs={12} data-testid="rna-target-panel">
            {panelPicker}
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            size="small"
            label="Same tumour block as"
            value={specimenFrom}
            onChange={(e) => onChange({ ...value, specimenFromAnalysisId: e.target.value, specimenFromChosen: true })}
            helperText={
              linkedAnalyses.length
                ? 'Shares that analysis’s tumour specimen and pairs the RNA with it.'
                : 'Name the DNA analysis of this tumour under "Other analyses of this patient" to pair them.'
            }
            inputProps={{ 'data-testid': 'library-specimen-from' }}
          >
            <MenuItem value="">
              <em>A specimen of its own</em>
            </MenuItem>
            {linkedAnalyses.map((analysis) => (
              <MenuItem key={analysis.analysis_id} value={String(analysis.analysis_id)}>
                {analysis.analysis_name || analysis.sample_name || `Analysis ${analysis.analysis_id}`}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </Box>
  )
}

LibraryFields.propTypes = {
  library: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  linkedAnalyses: PropTypes.array,
  capture: PropTypes.object,
  // The target panel picker, shown under the preparation when it needs a panel.
  panelPicker: PropTypes.node,
}

LibraryFields.defaultProps = {
  library: EMPTY_LIBRARY,
  errors: {},
  linkedAnalyses: [],
  capture: RNA_CAPTURE_OFF,
  panelPicker: null,
}

export default LibraryFields
